import { Component, OnDestroy, OnInit } from '@angular/core';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { HeaderComponent } from '../header/header.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NAVIGATION_ITEMS, NavItem } from '../../core/navigation/navigation.config';
import { SubmissionStartChoiceComponent } from '../../../shared/submission-start-choice/submission-start-choice.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonService } from '../../core/common/common.service';
import { SubmissionsService } from '../../core/submissions/submissions.service';
import { ProducerFlowModalComponent } from '../../../shared/modals/producer-flow-modal/producer-flow-modal.component';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { SupportComponent } from '@support/bp-pf-support';
import { environment } from '../../../environments/environment';
import { RoleService } from '../../core/others/role/role.service';
import { Auth0WrapperService } from '@auth/bp-pf-auth';


interface ProducerFlowUserData {
  agent_id?: string;
  agent_name?: string;
  first_name?: string;
  last_name?: string;
  agency_name?: string;
  agency_npn?: string;
  npn?: string;
  license_status?: string;
  email_address?: string;
  support_email?: string;
  support_hours?: string;
  turnaround_text?: string;
  role?: string;
  is_appointed?: string;
  producerflow_onboarding_url?: string;
}

interface ProducerFlowUserResponse {
  success?: boolean;
  message?: string;
  data?: ProducerFlowUserData;
}

@Component({
  selector: 'app-mainpage',
  standalone: true,
  imports: [SidebarComponent, HeaderComponent, RouterOutlet, CommonModule, SupportComponent],
  templateUrl: './mainpage.component.html',
  styleUrl: './mainpage.component.scss'
})
export class MainpageComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private isProducerFlowModalOpen = false;
  private isUserNotFoundModalOpen = false;
  public isProducerFlowCheckInProgress = false;
  public canRenderProtectedContent = true;
  public producerFlowDisplayMode: 'popup' | 'layout' = 'popup';
  public producerFlowViewModel: any = {};
  public isSidebarOpen: boolean = true;
  public sidebarWidth: number = 70;
  navItems: NavItem[] = NAVIGATION_ITEMS;

  isSupportOpen = false;
  appId: string = environment.app_id;

  constructor(
    private modalService: NgbModal,
    private commonService: CommonService,
    private router: Router,
    private submissionsService: SubmissionsService,
    private roleService: RoleService,
    private auth: Auth0WrapperService
  ) { }

  ngOnInit(): void {
    this.runProducerFlowCheck(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.runProducerFlowCheck(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Receives sidebar open/close events from child
   * and updates layout width accordingly.
   */
  public onSidebarToggle(isOpen: boolean): void {
    this.isSidebarOpen = isOpen;
    this.sidebarWidth = isOpen ? 250 : 70;
  }

  /** Current year (for footer display) */
  public get currentYear(): number {
    return new Date().getFullYear();
  }

  handleHeaderAction(event: { action: string; context?: any }): void {
    if (event.action === 'openSubmissionModal') {
      this.openCreateSubmissionPopup();
      return;
    }

    if (event.action) {
      this.commonService.emitHeaderAction(event.action, event.context);
    }
  }

  private openCreateSubmissionPopup(): void {
    const modalRef = this.modalService.open(SubmissionStartChoiceComponent, {
      centered: true,
      backdrop: 'static',
      size: 'xl',
       keyboard: false,
    });
  }

  private runProducerFlowCheck(url: string): void {
    if (!this.isProtectedMainRoute(url)) {
      this.isProducerFlowCheckInProgress = false;
      this.canRenderProtectedContent = true;
      return;
    }

    if (this.isProducerFlowModalOpen) {
      this.isProducerFlowCheckInProgress = false;
      this.canRenderProtectedContent = false;
      return;
    }

    this.isProducerFlowCheckInProgress = true;
    this.canRenderProtectedContent = false;

    this.submissionsService.producerFlowCheckUserDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProducerFlowUserResponse) => {
          if (this.shouldShowUserNotFoundPopup(response)) {
            this.canRenderProtectedContent = true;
            this.isProducerFlowCheckInProgress = false;
            this.openUserNotFoundPopup();
            return;
          }

          const data = response?.data;
          const role = (data?.role || '').toLowerCase();
          const isBlockedAgent = role === 'agent' && !this.isAppointed(data?.is_appointed);

          if (!isBlockedAgent || !data?.producerflow_onboarding_url) {
            this.canRenderProtectedContent = true;
            this.isProducerFlowCheckInProgress = false;
            return;
          }

          this.producerFlowViewModel = {
            onboardingUrl: data.producerflow_onboarding_url,
            agentId: data.agent_id || '-',
            agentName: data.first_name + ' ' + data.last_name || '-',
            agencyName: data.agency_name || '-',
            npn: data.agency_npn || '-',
            licenseStatus: data.license_status || '-',
            email: data.email_address || '-',
            supportEmail: data.support_email || 'help@greenshieldrisk.com',
            supportHours: data.support_hours || '-',
            turnaroundText: data.turnaround_text || '2-3 business days'
          };

          this.canRenderProtectedContent = false;
          this.isProducerFlowCheckInProgress = false;

          if (this.producerFlowDisplayMode !== 'popup') {
            return;
          }

          this.isProducerFlowModalOpen = true;
          const modalRef = this.modalService.open(ProducerFlowModalComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            size: 'lg'
          });

          modalRef.componentInstance.displayMode = 'popup';
          modalRef.componentInstance.onboardingUrl = data.producerflow_onboarding_url;
          modalRef.componentInstance.agentId = this.producerFlowViewModel.agentId;
          modalRef.componentInstance.agentName = this.producerFlowViewModel.agentName;
          modalRef.componentInstance.agencyName = this.producerFlowViewModel.agencyName;
          modalRef.componentInstance.npn = this.producerFlowViewModel.npn;
          modalRef.componentInstance.email = this.producerFlowViewModel.email;
          modalRef.componentInstance.licenseStatus = this.producerFlowViewModel.licenseStatus;
          modalRef.componentInstance.supportEmail = this.producerFlowViewModel.supportEmail;
          modalRef.componentInstance.supportHours = this.producerFlowViewModel.supportHours;
          modalRef.componentInstance.turnaroundText = this.producerFlowViewModel.turnaroundText;

          modalRef.result
            .catch(() => { })
            .finally(() => {
              this.isProducerFlowModalOpen = false;
            });
        },
        error: (error: unknown) => {
          if (this.shouldShowUserNotFoundPopup(error)) {
            this.canRenderProtectedContent = true;
            this.isProducerFlowCheckInProgress = false;
            this.openUserNotFoundPopup();
            return;
          }

          // Do not block the application flow when user-details lookup fails.
          this.canRenderProtectedContent = true;
          this.isProducerFlowCheckInProgress = false;
        }
      });
  }

  private shouldShowUserNotFoundPopup(responseOrError: ProducerFlowUserResponse | unknown): boolean {
    if (!this.isUwContext()) {
      return false;
    }

    const payload = this.getUserDetailsPayload(responseOrError);
    const message = (payload?.message || '').trim().toLowerCase();
    return payload?.success === false && message.includes('user not found');
  }

  private getUserDetailsPayload(responseOrError: ProducerFlowUserResponse | unknown): ProducerFlowUserResponse | null {
    if (!responseOrError || typeof responseOrError !== 'object') {
      return null;
    }

    const source = responseOrError as any;
    let payload = source.error ?? source;

    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload);
      } catch {
        return null;
      }
    }

    if (payload?.error && typeof payload.error === 'object') {
      payload = payload.error;
    }

    return payload && typeof payload === 'object' ? payload as ProducerFlowUserResponse : null;
  }

  private isUwContext(): boolean {
    const currentRole = (this.roleService.role || '').toLowerCase();
    if (currentRole === 'uw_assist' || currentRole === 'uw_assistant' || currentRole === 'underwriter') {
      return true;
    }

    const path = (this.router.url || '').split('?')[0].toLowerCase();
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const segments = normalizedPath.split('/').filter(Boolean);
    const childRoute = segments[1] || '';

    return childRoute === 'work-view';
  }

  private openUserNotFoundPopup(): void {
    if (this.isUserNotFoundModalOpen) {
      return;
    }

    this.isUserNotFoundModalOpen = true;
    const modalRef = this.modalService.open(ConfirmDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md'
    });

    modalRef.componentInstance.title = '';
    modalRef.componentInstance.message = 'Having trouble signing in? If you’re a new Green Shield user, please reach out to compass@greenshieldrisk.com for assistance.';
    modalRef.componentInstance.confirmText = 'OK';
    modalRef.componentInstance.showCancel = false;

    modalRef.result
      .then((result) => {
        if (result === 'confirm') {
          this.auth.logoutWithRedirect();
        }
      })
      .catch(() => { })
      .finally(() => {
        this.isUserNotFoundModalOpen = false;
      });
  }

  private isAppointed(value: string | any): boolean {
    return String(value || '').trim().toUpperCase() === 'APPOINTED';
  }

  private isProtectedMainRoute(url: string): boolean {
    const path = (url || '').split('?')[0].toLowerCase();
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const segments = normalizedPath.split('/').filter(Boolean);

    // Trigger check on initial landing route as well (e.g. "/" or "/{tenant}").
    if (segments.length <= 1) {
      return true;
    }

    const currentRole = (this.roleService.role || '').toLowerCase();
    const childRoute = segments[1] || '';

    if (childRoute.startsWith('submission')) {
      return true;
    }

    if (currentRole === 'agent') {
      return childRoute === 'dashboard';
    }

    if (currentRole === 'uw_assist' || currentRole === 'uw_assistant' || currentRole === 'underwriter') {
      return childRoute === 'work-view';
    }

    return false;
  }
}
